import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, IconButton, InputAdornment, Paper, TextField, Typography } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Login from "../../components/Modal/Login/Login";
import ForgotPassword from "../../components/Modal/ForgotPassword/ForgotPassword";
import useResetPassword from "../../hooks/useResetPassword";
import { useAlert } from "../../context/AlertContext";

const GOLD = "#D4AF37";
// Mesma regra do backend (bcrypt usa só os primeiros 72 bytes)
const MIN_LENGTH = 8;
const MAX_LENGTH = 72;

const goldButton = {
    color: GOLD,
    borderColor: GOLD,
    fontWeight: "bold",
    "&:hover": { backgroundColor: GOLD, color: "#fff", borderColor: GOLD },
};

const RedefinirSenha = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const { resetPassword, response, loading, error } = useResetPassword();

    // O token é lido uma vez e a URL é limpa logo em seguida, para não ficar
    // na barra de endereço, no histórico, no Referer nem no analytics
    const [token] = useState(() => searchParams.get("code"));
    const [form, setForm] = useState({ password: "", confirm: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [done, setDone] = useState(false);
    const [failed, setFailed] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);
    const [forgotOpen, setForgotOpen] = useState(false);

    useEffect(() => {
        if (searchParams.has("code")) {
            navigate("/redefinir-senha", { replace: true });
        }
    }, []);

    useEffect(() => {
        if (response) {
            setDone(true);
            setForm({ password: "", confirm: "" });
            showAlert("Senha redefinida com sucesso!", "success");
        }
    }, [response]);

    useEffect(() => {
        if (error) {
            setFailed(true);
            showAlert(error, "error");
        }
    }, [error]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (loading) return;

        if (!form.password || !form.confirm) {
            showAlert("Preencha os dois campos de senha", "warning");
            return;
        }
        if (form.password.length < MIN_LENGTH || form.password.length > MAX_LENGTH) {
            showAlert(`A senha deve ter entre ${MIN_LENGTH} e ${MAX_LENGTH} caracteres`, "warning");
            return;
        }
        if (form.password !== form.confirm) {
            showAlert("As senhas não conferem", "warning");
            return;
        }

        resetPassword({ resetToken: token, newPassword: form.password });
    };

    const passwordAdornment = (
        <InputAdornment position="end">
            <IconButton
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                onClick={() => setShowPassword((prev) => !prev)}
                edge="end"
            >
                {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
        </InputAdornment>
    );

    const renderContent = () => {
        if (done) {
            return (
                <>
                    <Typography align="center" sx={{ mb: 3 }}>
                        Sua senha foi redefinida. Agora é só entrar com a nova senha.
                    </Typography>
                    <Button fullWidth variant="outlined" sx={goldButton} onClick={() => setLoginOpen(true)}>
                        Entrar
                    </Button>
                </>
            );
        }

        if (!token) {
            return (
                <>
                    <Typography align="center" sx={{ mb: 3 }}>
                        Este link de redefinição é inválido ou está incompleto. Solicite um novo link para continuar.
                    </Typography>
                    <Button fullWidth variant="outlined" sx={goldButton} onClick={() => setForgotOpen(true)}>
                        Solicitar novo link
                    </Button>
                </>
            );
        }

        return (
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <Typography align="center" sx={{ mb: 2 }}>
                    Digite sua nova senha. Ela deve ter pelo menos {MIN_LENGTH} caracteres.
                </Typography>
                <TextField
                    fullWidth
                    margin="normal"
                    label="Nova senha"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={form.password}
                    onChange={handleChange}
                    InputProps={{ endAdornment: passwordAdornment }}
                />
                <TextField
                    fullWidth
                    margin="normal"
                    label="Confirmar senha"
                    name="confirm"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={form.confirm}
                    onChange={handleChange}
                />
                <Button type="submit" fullWidth variant="outlined" disabled={loading} sx={{ ...goldButton, mt: 2 }}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Redefinir senha"}
                </Button>

                {failed && (
                    <Button fullWidth sx={{ mt: 1, color: "#707070" }} onClick={() => setForgotOpen(true)}>
                        Solicitar novo link
                    </Button>
                )}
            </Box>
        );
    };

    return (
        <>
            <NavBar variant="default" />
            <Box sx={{ display: "flex", justifyContent: "center", px: 2, py: { xs: 4, sm: 8 } }}>
                <Paper
                    elevation={3}
                    sx={{
                        width: "100%",
                        maxWidth: 460,
                        p: { xs: 3, sm: 4 },
                        borderRadius: "12px",
                        borderRight: "18px solid #d6b827",
                    }}
                >
                    <Typography
                        variant="h5"
                        align="center"
                        sx={{ color: GOLD, fontWeight: "bold", textTransform: "uppercase", mb: 2 }}
                    >
                        Redefinir senha
                    </Typography>
                    {renderContent()}
                </Paper>
            </Box>
            <Footer />

            <Login open={loginOpen} handleClose={() => setLoginOpen(false)} />
            <ForgotPassword open={forgotOpen} handleClose={() => setForgotOpen(false)} />
        </>
    );
};

export default RedefinirSenha;
